"use client";

import React, { useState } from "react";
import {
  Container,
  Title,
  Text,
  TextInput,
  Textarea,
  Checkbox,
  Button,
  Grid,
  Stack,
  Group,
  Box,
  Alert,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { djangoAxiosInstance } from "../../../api";
import { endpoints } from "../../../api/constants/endpoints";

const ContactSection = () => {
  const form = useForm({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      message: "",
      agreed: false,
    },

    validate: {
      first_name: (value) =>
        value.trim().length < 1 ? "First name is required" : null,
      last_name: (value) =>
        value.trim().length < 1 ? "Last name is required" : null,
      email: (value) =>
        /^\S+@\S+$/.test(value) ? null : "Invalid email address",
      message: (value) =>
        value.trim().length < 10
          ? "Message should be at least 10 characters"
          : null,
      agreed: (value) =>
        value ? null : "You must agree to the privacy policy",
    },
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleSubmit = async (values) => {
    try {
      setStatus({ submitting: true, success: false, error: null });

      const payload = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        message: values.message,
        agreed_to_policy: values.agreed,
      };

      const response = await djangoAxiosInstance.post(
        endpoints.CONTACTS,
        payload,
      );

      setStatus({
        submitting: false,
        success: true,
        error: null,
      });

      form.reset();
    } catch (error) {
      const fieldErrors = error.response.data;

      const formattedErrors = {};
      Object.keys(fieldErrors).forEach((key) => {
        formattedErrors[key] = Array.isArray(fieldErrors[key])
          ? fieldErrors[key][0]
          : fieldErrors[key];
      });

      form.setErrors(formattedErrors);
      setStatus({
        submitting: false,
        success: false,
        error: "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <Box py={{ base: 40, sm: 60 }}>
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: "-10rem",
          left: 0,
          right: 0,
          zIndex: -1,
          overflow: "hidden",
          filter: "blur(3rem)",
          "&::before": {
            content: '""',
            position: "absolute",
            left: "50%",
            aspectRatio: "1155/678",
            width: "36.125rem",
            maxWidth: "none",
            transform: "translateX(-50%) rotate(30deg)",
            background: `linear-gradient(to right top, ${theme.colors.pink[4]}, ${theme.colors.indigo[4]})`,
            opacity: 0.3,
            clipPath:
              "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
          },
        })}
      />

      <Container size="sm">
        <Stack align="center" mb={40}>
          <Title order={2} ta="center" size="h1" fw={600}>
            Contact Sales
          </Title>
          <Text size="lg" c="dimmed" ta="center">
            We're here to help with any questions about our products and
            services.
          </Text>
        </Stack>

        {status.success && (
          <Alert
            icon={<IconCheck size={16} />}
            title="Success!"
            color="green"
            radius="md"
            mb="xl"
          >
            Thank you for reaching out! We'll get back to you shortly.
          </Alert>
        )}

        {status.error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            radius="md"
            mb="xl"
          >
            {status.error}
          </Alert>
        )}

        <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
          <Grid gutter="xl">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="First name"
                placeholder="Your first name"
                withAsterisk
                {...form.getInputProps("first_name")}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Last name"
                placeholder="Your last name"
                withAsterisk
                {...form.getInputProps("last_name")}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <TextInput
                label="Email"
                placeholder="your.email@example.com"
                withAsterisk
                {...form.getInputProps("email")}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <Textarea
                label="Message"
                placeholder="How can we help you?"
                rows={4}
                withAsterisk
                {...form.getInputProps("message")}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <Checkbox
                label={
                  <Text size="sm">
                    I agree to receive marketing emails from Socius Legal.
                  </Text>
                }
                {...form.getInputProps("agreed", { type: "checkbox" })}
                error={form.errors.agreed}
              />
            </Grid.Col>
          </Grid>

          <Group justify="center" mt="xl">
            <Button
              type="submit"
              fullWidth
              loading={status.submitting}
              size="md"
            >
              {status.submitting ? "Submitting..." : "Let's talk"}
            </Button>
          </Group>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactSection;
